import { useArrayIncludes } from "@vueuse/core";
import { FirebaseApp } from "firebase/app";
import { Firestore, collection, getDoc, getDocs, limit, query, where } from "firebase/firestore";

import * as Sentry from "@sentry/vue";
import { Timestamp } from "firebase/firestore/lite";

export interface SearchOptions {
  db: Firestore
  path: string;
  searchText: string;
  fields: SearchField[];
  converter: any;
  limitBy: number;
}

export interface SearchField {
  name: string;
  type: string;
}

export const combinedTextSearch = async (options: SearchOptions) => {
  const { db, path, searchText, fields, converter, limitBy } = options;
  const colRef = collection(db, path);

  const combinedResults: any[] = [];
  
  for await (const field of fields) {
    try {
      if (field.type === 'array') {
        const q = query(
          colRef,
          where(field.name, "array-contains", searchText),
          limit(limitBy)
        ).withConverter(converter)
        const results = await getDocs(q);
        if (results.docs.length > 0) {
          results.forEach((doc) => {
            combinedResults.push({...doc.data(), id: doc.id})
          })
        }
      } else {
        const q = query(
          colRef,
          where(field.name, '>=', searchText),
          where(field.name, '<=', searchText + 'z'),
          limit(limitBy)
        ).withConverter(converter)
        const results = await getDocs(q);
        if (results.docs.length > 0) {
          results.forEach((doc) => {
            combinedResults.push({...doc.data(), id: doc.id})
          })
        }
      }
      
    } catch (error) {
      console.error(error)
      Sentry.captureException(error);
    }
  }

  // Make resutls unique and return
  return combinedResults.filter((v, i, a) => a.findIndex(t => (t.id === v.id)) === i);
}

/**
 * Sanitizes data for Firestore operations by removing undefined and null values.
 * Firestore doesn't accept undefined values and will throw an error.
 * 
 * @param data - The data object to sanitize
 * @returns A new object with undefined/null values removed
 */
function isPlainObject(obj: any): obj is Record<string, any> {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    Object.prototype.toString.call(obj) === '[object Object]'
  );
}

export function sanitizeForFirestoreUpdate<T extends Record<string, any>>(data: T): Partial<T> {
  const sanitized: Partial<T> = {};

  for (const [key, value] of Object.entries(data)) {
    if (value === undefined || value === null) {
      sanitized[key as keyof T] = null as any;
    } else if (value instanceof Timestamp) {
      (sanitized as any)[key as keyof T] = value.toDate();
    } else if (
      isPlainObject(value) &&
      typeof value.seconds === 'number' &&
      typeof value.nanoseconds === 'number'
    ) {
      (sanitized as any)[key as keyof T] = new Timestamp(value.seconds, value.nanoseconds).toDate();
    } else if (Array.isArray(value)) {
      (sanitized as any)[key as keyof T] = value.map(item => {
        if (isPlainObject(item) || Array.isArray(item)) {
          return sanitizeForFirestoreUpdate(item as Record<string, any>);
        }
        return item;
      });
    } else if (isPlainObject(value)) {
      sanitized[key as keyof T] = sanitizeForFirestoreUpdate(value) as any;
    } else {
      sanitized[key as keyof T] = value;
    }
  }

  return sanitized;
}